<template>
  <ValidationProvider
    :ref="name"
    :name="name"
    :vid="name"
    :rules="rules"
    :mode="validationMode"
    slim
    v-slot="{ errors }"
  >
    <v-autocomplete
      v-model="model"
      :items="locations"
      :loading="loading"
      :search-input.sync="search"
      :name="name"
      :label="`${label} ${requiredHint}`"
      :placeholder="placeholder"
      :error-messages="errors[0]"
      :no-data-text="$t('empty')"
      item-text="name"
      item-value="id"
      :style="customStyle"
      no-filter
    />
  </ValidationProvider>
</template>

<script>
import _ from "lodash";
import {mapGetters} from "vuex";
import {BASE_API_URL} from "@/store";
import {getFieldRules} from "@/utils/auth/validation";

export default {
  props: {
    component: {
      type: Object,
      required: true
    },
    value: {
      type: [String, Number, Object],
      default: null
    },
    category: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      locations: [],
      loading: false,
      search: null
    };
  },
  computed: {
    ...mapGetters(["client_id"]),
    normalizedValue() {
      if (this.value && typeof this.value === "object") {
        return this.value.id || this.value.value || null;
      }
      return this.value;
    },
    model: {
      get() {
        return this.normalizedValue;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    name() {
      return _.get(this.component, "name", "");
    },
    label() {
      return _.get(this.component, "label", "");
    },
    placeholder() {
      return _.get(this.component, "placeholder", "");
    },
    requiredHint() {
      return _.get(this.component, "required", false) ? "*" : "";
    },
    rules() {
      return getFieldRules(this.component);
    },
    validationMode() {
      return _.get(this.component, "validationMode", "eager");
    },
    customStyle() {
      return _.get(this.component, "props.style", {});
    },
    selectedLocationName() {
      const selected = this.locations.find((l) => l.id === this.normalizedValue);
      return selected ? selected.name : null;
    }
  },
  watch: {
    search(val) {
      // When Vuetify sets search to the selected item's name after selection,
      // fetch all options so the full list is visible when reopening the dropdown
      if (val === this.selectedLocationName) {
        this.debouncedFetchLocations("");
      } else {
        this.debouncedFetchLocations(val || "");
      }
    }
  },
  async created() {
    this.debouncedFetchLocations = _.debounce(this.fetchLocations, 200);

    await this.fetchLocations();

    if (this.normalizedValue && !this.locations.some((l) => l.id === this.normalizedValue)) {
      const selected = await this.fetchLocationById(this.normalizedValue);
      if (selected) {
        this.locations = [selected, ...this.locations];
      }
    }

    this.$emit("field-registered", this.name);
  },
  methods: {
    buildParams(search) {
      const params = {limit: 20};
      if (search) { params.search = search; }
      if (this.category) { params.category = this.category; }
      return params;
    },
    async fetchLocations(search) {
      this.loading = true;
      try {
        const response = await this.axios.get(
          `${BASE_API_URL}/api/v1/clients/${this.client_id}/locations/`,
          {params: this.buildParams(search)}
        );
        const raw = _.get(response, "data.results", response.data);
        const results = Array.isArray(raw) ? raw : [];

        // Keep the selected location in the list if the new results don't include it
        if (this.normalizedValue && !results.some((l) => l.id === this.normalizedValue)) {
          const cached = this.locations.find((l) => l.id === this.normalizedValue);
          this.locations = cached ? [cached, ...results] : results;
        } else {
          this.locations = results;
        }
      } catch {
        this.locations = [];
      } finally {
        this.loading = false;
      }
    },
    async fetchLocationById(id) {
      try {
        const response = await this.axios.get(
          `${BASE_API_URL}/api/v1/clients/${this.client_id}/locations/`,
          {params: {id}}
        );
        const raw = _.get(response, "data", []);
        const list = Array.isArray(raw) ? raw : _.get(raw, "results", []);
        return list[0] && list[0].id ? list[0] : null;
      } catch {
        return null;
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
